import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Bot, Coins, Compass, Quote, ShieldCheck, Sparkles } from "lucide-react";
import { Logo } from "@/components/Logo";

const MENTOR = "https://static.prod-images.emergentagent.com/jobs/0c62e012-1fb3-4426-a0f5-6bb9c23d9db4/images/1cf0d8902605240025d52857147216938606933ba6fb6809e1ef9f2b69edcb84.jpeg";

const steps = [
  { icon: Compass, title: "Tell us your goal", note: "Your age, where you're really starting from, and what you want — in your own words." },
  { icon: BookOpen, title: "Get your curriculum", note: "Subject → Chapter → Topic, reasoned from your goal and anchored to your grade level." },
  { icon: Sparkles, title: "Learn in cards", note: "One idea at a time. Understood, explain again, or ask — the teacher answers." },
  { icon: ShieldCheck, title: "Prove it", note: "Practice across four difficulty tiers, then topic, chapter and subject assessments." },
];

const agents = [
  ["Curriculum Advisor", "Draws the plan and reshapes it whenever you edit it."],
  ["Teacher", "Writes the material and the cards, and re-explains until it lands."],
  ["Research", "Retrieves real papers, books and references — never invented ones."],
  ["Practice", "Builds the difficulty ladder and marks your answers."],
  ["Daily Planner", "Shapes each day and calls your pace honestly."],
  ["Real-World", "Connects the chapter to things you actually care about."],
];

export default function Landing() {
  return (
    <div className="min-h-screen grain">
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between relative z-10">
        <Logo size="md" testId="landing-logo" />
        <div className="flex items-center gap-2">
          <Link to="/about" data-testid="landing-about" className="hidden sm:block px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200/60 rounded-full transition-colors">
            About
          </Link>
          <Link to="/login" data-testid="landing-login" className="px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-200/60 rounded-full transition-colors">
            Sign in
          </Link>
          <Link to="/signup" data-testid="landing-signup" className="px-5 py-2 text-sm font-semibold bg-slate-900 text-white rounded-full hover:bg-slate-800 transition-colors">
            Start learning
          </Link>
        </div>
      </header>

      <main className="relative z-10">
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 pt-8 pb-20 items-center">
          <div className="lg:col-span-6 reveal">
            <p className="rule-label">Start where you actually are</p>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.04]">
              You own the journey.
              <br />
              <span className="text-blue-700">A patient teacher walks it with you.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed">
              Tell Unbound where you're really starting from — even if that's nowhere at all. A council of AI agents
              draws your curriculum, teaches it one card at a time, and cites real sources. No school required, no
              guardian required, nobody's approval but yours.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link to="/signup" data-testid="landing-cta" className="group inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-colors">
                Set my goal
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link to="/about" data-testid="landing-secondary" className="inline-flex items-center gap-2 px-6 py-3 border border-slate-300 rounded-full font-semibold text-slate-700 hover:border-slate-500 transition-colors">
                See how it works
              </Link>
            </div>
            <dl className="mt-12 grid sm:grid-cols-3 gap-8 border-t border-slate-200 pt-8">
              {[
                ["Free", "Genuinely, with fair-use limits"],
                ["Sourced", "arXiv · Open Library · encyclopaedia"],
                ["Yours", "Add or drop subjects any time"],
              ].map(([k, v]) => (
                <div key={k}>
                  <dt className="font-display font-bold text-lg">{k}</dt>
                  <dd className="text-sm text-slate-500 mt-1">{v}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="lg:col-span-6 reveal" style={{ animationDelay: "120ms" }}>
            <figure className="relative">
              <img
                src={MENTOR}
                alt="An elderly teacher sitting beneath a banyan tree, explaining something patiently"
                data-testid="landing-hero-image"
                className="w-full rounded-3xl border border-slate-200 shadow-[0_30px_80px_-40px_rgba(15,23,42,0.45)] object-cover aspect-[4/3]"
              />
              <figcaption className="absolute -bottom-6 left-6 right-6 sm:left-10 sm:right-10 p-5 rounded-2xl bg-[#faf8f5]/95 backdrop-blur border border-slate-200">
                <Quote className="w-4 h-4 text-blue-600" />
                <p className="mt-2 font-display text-base sm:text-lg font-semibold leading-snug">
                  Patience. Depth. One idea at a time — and the next one only when the last one lands.
                </p>
              </figcaption>
            </figure>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 mt-10">
          <p className="rule-label">How it works</p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold">Four steps, then you're studying</h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((s, i) => (
              <div key={s.title} className="card-flat p-6 reveal" style={{ animationDelay: `${i * 80}ms` }}>
                <s.icon className="w-5 h-5 text-blue-600" />
                <h3 className="mt-4 font-display text-lg font-semibold">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">{s.note}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-slate-200">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <p className="rule-label">The council</p>
              <h2 className="mt-3 text-3xl font-extrabold">Six agents, one learner</h2>
              <p className="mt-4 text-sm text-slate-600 leading-relaxed">
                Each agent does one job well, and every prompt is inspectable. Guardians can add guidance; learners aged
                13+ can add their own. The safety layer is immutable.
              </p>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-2 gap-4">
              {agents.map(([name, note]) => (
                <div key={name} className="flex gap-3 p-5 card-flat">
                  <Bot className="w-4 h-4 mt-0.5 text-slate-400 shrink-0" />
                  <div>
                    <p className="font-semibold text-sm">{name}</p>
                    <p className="mt-1 text-sm text-slate-500">{note}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-slate-200">
          <div className="card-flat p-8 sm:p-10 flex flex-col lg:flex-row gap-8 items-start">
            <Coins className="w-6 h-6 text-blue-600 shrink-0" />
            <div>
              <h2 className="font-display text-2xl sm:text-3xl font-extrabold">Free. Genuinely.</h2>
              <p className="mt-3 text-sm sm:text-base text-slate-600 max-w-2xl leading-relaxed">
                No fees, no card, no trial clock. Generating lessons costs real money, so every learner has a generous
                daily and monthly AI allowance, and the platform has an overall ceiling. Reach a limit and everything
                you've already got stays yours to study — the allowance resets. That honesty is what keeps this free for
                the learners who need it most.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/terms" className="text-sm font-semibold text-blue-700 hover:underline">Terms &amp; Conditions</Link>
                <Link to="/privacy" className="text-sm font-semibold text-blue-700 hover:underline">Privacy Policy</Link>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center border-t border-slate-200">
          <p className="rule-label">Go well</p>
          <h2 className="mt-4 text-3xl sm:text-5xl font-extrabold max-w-3xl mx-auto leading-[1.08]">
            Wherever you're starting from, that's where we start.
          </h2>
          <div className="mt-9 flex flex-wrap justify-center gap-3">
            <Link to="/signup" data-testid="landing-cta-bottom" className="inline-flex items-center gap-2 px-7 py-3.5 bg-slate-900 text-white rounded-full font-semibold hover:bg-slate-800 transition-colors">
              Begin <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/about" className="inline-flex items-center gap-2 px-7 py-3.5 border border-slate-300 rounded-full font-semibold text-slate-700 hover:border-slate-500 transition-colors">
              See how it looks
            </Link>
          </div>
          <div className="mt-14 pt-8 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
            <Logo testId="landing-logo-footer" />
            <p className="text-xs text-slate-400">Learning belongs to the learner.</p>
          </div>
        </section>
      </main>
    </div>
  );
}
